import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ProfileHeader from "../ProfileHeader/ProfileHeader";
import profile from "../../data/profiles/reclaimedByNature.json";
import { processAndUploadImages } from "../../services/imageService";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "./SortableItem";
import "./PostForm.css";

const PostForm = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [history, setHistory] = useState([]); // Stores previous versions of the files array
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [location, setLocation] = useState("TBA");
  // State for the post preview
  const [previewData, setPreviewData] = useState(null);

  // Function to allow multiple image re-ordering
  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };
  // Helper to save current state to history before a change
  const HISTORY_LIMIT = 2;

  const saveHistory = () => {
    setHistory((prev) => {
      const newHistory = [...prev, [...files]];
      // If we exceed the limit, remove the oldest entry (index 0)
      if (newHistory.length > HISTORY_LIMIT) {
        return newHistory.slice(1);
      }
      return newHistory;
    });
  };
  const undo = () => {
    if (history.length === 0) return;

    const previousState = history[history.length - 1];
    setFiles(previousState);
    setHistory((prev) => prev.slice(0, -1));

    // Optional: Simple feedback
    console.log("Action Undone");
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+Z or Ctrl+Z
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        // Prevent browser default undo if we are in a text field
        if (e.target.tagName !== "TEXTAREA" && e.target.tagName !== "INPUT") {
          e.preventDefault();
          undo();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [history, files]); // Re-bind when history/files change

  // function to sync the form to the preview
  const updatePreview = (e) => {
    // Get the form context
    const form = e.target.form;
    const rawTags = e.target.value;
    const isStyled = form.elements.isStyled.checked;

    const formattedTags = rawTags
      .split(",")
      .filter((t) => t.includes(":"))
      .map((tag) => {
        const [t, h] = tag.split(":");
        return { title: t.trim(), handle: h.trim() };
      });

    if (isStyled) {
      formattedTags.unshift({
        title: "stylist",
        handle: "reclamado.por.la.naturaleza",
      });
    }

    setPreviewData({
      title: form.elements.title.value,
      location: form.elements.location.value,
      model: form.elements.model.value,
      tags: formattedTags,
      mediaCount: files.length, // Show how many photos are ready
    });
  };

  // High-precision sensors to allow clicking buttons inside draggable items
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files).map((file) => ({
      id: URL.createObjectURL(file), // Unique ID for DND
      file: file,
      preview: URL.createObjectURL(file),
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).map((file) => ({
        id: URL.createObjectURL(file),
        file: file,
        preview: URL.createObjectURL(file),
      }));

      setFiles((prev) => [...prev, ...droppedFiles]);
      e.dataTransfer.clearData();
    }
  };

  // You also need these to prevent the browser from simply opening the image file
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    saveHistory(); // Save BEFORE moving
    setFiles((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);

      // If we're dragging a selected item, move the whole group
      if (selectedIds.includes(active.id)) {
        const selectedItems = items.filter((i) => selectedIds.includes(i.id));
        const remainingItems = items.filter((i) => !selectedIds.includes(i.id));

        // Re-insert the selected block at the new position
        const newItems = [...remainingItems];
        newItems.splice(newIndex, 0, ...selectedItems);
        return newItems;
      }

      // Otherwise, just move the single item (standard behavior)
      return arrayMove(items, oldIndex, newIndex);
    });

    // Clear selection after move
    setSelectedIds([]);
  };

  const removeFile = (id) => {
    saveHistory(); // Lock in the current state before deleting
    setFiles((prev) => prev.filter((f) => f.id !== id));
    URL.revokeObjectURL(id);
  };

  // Prevent premature form submission from enter key
  const handleKeyDown = (e) => {
    // Check if the pressed key is Enter and NOT inside a textarea
    // (We want Enter to work in the Caption textarea for new lines)
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      return;
    }
    setLoading(true);
    setProgress(0);

    // Grab values directly from the form elements
    const form = e.currentTarget;
    const title = form.elements.title.value;
    const location = form.elements.location.value;
    const model = form.elements.model.value;
    const rawTags = form.elements.tags.value;

    try {
      console.log("1. Starting compression and upload...");
      const orderedFiles = files.map((f) => f.file);
      const uploadedMedia = await processAndUploadImages(
        orderedFiles,
        (current, total) => {
          const percent = Math.round((current / total) * 100);
          setProgress(percent);
          setStatusText(`Processing image ${current} of ${total}...`);
        },
      );

      // Transform tags: "stylist:handle, makeup:handle" -> [{title, handle}]
      const formattedTags = rawTags
        .split(",")
        .filter((t) => t.includes(":"))
        .map((tag) => {
          const [t, h] = tag.split(":");
          return { title: t.trim(), handle: h.trim() };
        });
      const isStyled = form.elements.isStyled.checked;

      if (isStyled) {
        formattedTags.unshift({
          title: "stylist",
          handle: "reclamado.por.la.naturaleza",
        });
      }

      const newPost = {
        media: uploadedMedia.filter((m) => m !== null),
        title: title,
        creation_timestamp: Math.floor(Date.now() / 1000),
        location: location,
        model: model,
        tags: formattedTags,
      };

      console.log("2. Sending payload to Python:", newPost);

      const response = await fetch("http://127.0.0.1:8000/add-post", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newPost),
      });

      if (response.ok) {
        setStatusText("Done!");
        // Clear URLs from memory before leaving
        files.forEach((fileObj) => URL.revokeObjectURL(fileObj.preview));
        navigate("/");
      } else {
        const errorData = await response.json();
        console.error("Server Error:", errorData);
      }
    } catch (error) {
      console.error("Submission failed:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ProfileHeader profile={profile} />
      <form
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        className="post-form"
      >
        <div className="instagram-create-container">
          <div className="form-group">
            <label htmlFor="media">Media</label>
            {/* 1. Image Tray */}
            <div
              className="horizontal-tray-wrapper"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={files}
                  strategy={horizontalListSortingStrategy}
                >
                  <div className="horizontal-tray">
                    {files.map((fileObj) => (
                      <div
                        key={fileObj.id}
                        onClick={(e) => {
                          // Only toggle if they click the image, not the 'remove' button
                          if (e.target.tagName !== "BUTTON")
                            toggleSelection(fileObj.id);
                        }}
                        className={
                          selectedIds.includes(fileObj.id)
                            ? "selected-wrapper"
                            : ""
                        }
                      >
                        <SortableItem
                          id={fileObj.id}
                          url={fileObj.preview}
                          onRemove={removeFile}
                        />
                      </div>
                    ))}

                    {/* The "Add More" box at the end of the tray */}
                    <label
                      className="add-more-box"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                    >
                      <input
                        id="media"
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        hidden
                      />
                      <span>+</span>
                    </label>
                  </div>
                </SortableContext>
              </DndContext>
            </div>
            {loading && (
              <div className="progress-container">
                <div className="progress-info">
                  <span>{statusText}</span>
                  <span>{progress}%</span>
                </div>
                <div className="progress-bar-background">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
          {/* 2. Metadata Inputs */}
          <div className="form-fields">
            {previewData?.title !== undefined && (
              <div className="json-preview">
                <h3>Post Preview</h3>
                <pre>{JSON.stringify(previewData, null, 2)}</pre>
                <p style={{ fontSize: "12px", color: "#666" }}>
                  📸 {previewData.mediaCount} images ready for upload
                </p>
              </div>
            )}
            <div className="form-group">
              <label htmlFor="title">Caption</label>
              <textarea
                id="title"
                name="title"
                rows="4"
                className="caption-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                id="location"
                name="location"
                placeholder="Add Location"
                value={location}
                onChange={(e) => {
                  setLocation(e.target.value);
                }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="model">Model</label>
              <input id="model" name="model" placeholder="Tag Model" />
            </div>
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  id="isStyled"
                  name="isStyled"
                  className="styled-checkbox"
                />
                <span>Styled by Reclamado?</span>
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="location">Additional Tags</label>
              <input
                id="tags"
                name="tags"
                placeholder="e.g. Makeup:artist_handle (comma separated)"
                onChange={updatePreview}
              />
            </div>
          </div>
          <div>
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? "processing..." : "Create Post"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="cancel-button"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PostForm;
